import React, { useState, useEffect } from 'react';
import { companyService } from '../../api/apiService';
import '../../App.css';
import edit from '../../images/edit.svg';
import deleteIcon from '../../images/delete.svg';

function CompanyList() {
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentCompany, setCurrentCompany] = useState(null);
  
  // Form fields for editing
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentName, setCurrentName] = useState('');
  const [currentAddress, setCurrentAddress] = useState('');
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState('');
  
  // Form fields for creating new company
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await companyService.getAllCompanies(1, 1000);
      const companiesData = response?.data?.companies || response?.data || [];
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error fetching companies:', error);
      alert('Error loading companies');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const deleteCompany = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      try {
        await companyService.deleteCompany(id);
        alert('Company deleted successfully');
        fetchCompanies();
      } catch (error) {
        console.error('Error deleting company:', error);
        alert('Error deleting company: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const editCompany = (company) => {
    setCurrentCompany(company);
    setCurrentEmail(company.email || '');
    setCurrentName(company.name || '');
    setCurrentAddress(company.address || '');
    setCurrentPhoneNumber(company.phone_number || company.phone || '');
  };

  const updateCompany = async () => {
    try {
      const { id } = currentCompany;
      const updateData = {
        email: currentEmail,
        name: currentName,
        address: currentAddress,
        phone_number: currentPhoneNumber,
      };
      
      await companyService.updateCompany(id, updateData);
      alert('Company updated successfully');
      setCurrentCompany(null);
      fetchCompanies();
    } catch (error) {
      console.error('Error updating company:', error);
      alert('Error updating company: ' + (error.response?.data?.message || error.message));
    }
  };

  const addCompany = async (e) => {
    e.preventDefault();
    try {
      if (!companyName.trim()) {
        alert('Company name is required');
        return;
      }

      const companyData = {
        name: companyName.trim(),
        address: companyAddress.trim(),
        email: companyEmail.trim(),
        phone_number: companyPhone.trim()
      };

      await companyService.createCompany(companyData);
      alert("Company added successfully");

      // Reset form fields
      setCompanyName('');
      setCompanyAddress('');
      setCompanyEmail('');
      setCompanyPhone('');
      setShowCreateForm(false);

      // Refresh the list
      fetchCompanies();
    } catch (error) {
      console.error("Error adding company:", error);
      alert("Error adding company: " + (error.response?.data?.message || error.message));
    }
  };

  const sortByName = () => {
    const sorted = [...companies].sort((a, b) => {
      // Add null checks for sorting
      const nameA = (a.name || '').toString();
      const nameB = (b.name || '').toString();
      
      return sortOrder === "asc" 
        ? nameA.localeCompare(nameB) 
        : nameB.localeCompare(nameA);
    });
    setCompanies(sorted);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredCompanies = companies.filter(company => {
    if (!company) return false;
    
    const name = (company.name || '').toLowerCase();
    const email = (company.email || '').toLowerCase();
    const address = (company.address || '').toLowerCase();
    const searchTerm = search.toLowerCase();
    
    return name.includes(searchTerm) || 
           email.includes(searchTerm) || 
           address.includes(searchTerm);
  });

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Company List</h1>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px', marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="inputtext search"
            style={{ width: '300px' }}
          />
          <button
            className="big-button"
            onClick={() => setShowCreateForm(true)}
            style={{ textDecoration: "none", alignSelf: "start" }}
          >
            Add Company
          </button>
        </div>

        {/* Create Company Form */}
        {showCreateForm && (
          <div className="create-form" style={{ 
            border: '1px solid #28a745', 
            padding: '20px', 
            marginBottom: '20px',
            backgroundColor: '#f8fff9'
          }}>
            <h3>Add New Company</h3>
            
            <form onSubmit={addCompany}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    className="inputtext"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    className="inputtext"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    placeholder="Enter company email"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Address
                  </label>
                  <input
                    type="text"
                    className="inputtext"
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder="Enter company address"
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Phone Number
                  </label>
                  <input
                    type="text"
                    className="inputtext"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(e.target.value)}
                    placeholder="Enter phone number"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  className="button"
                  style={{ backgroundColor: '#28a745', color: 'white' }}
                >
                  Add Company
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={() => setShowCreateForm(false)}
                  style={{ backgroundColor: '#6c757d' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Edit Company Form */}
        {currentCompany && (
          <div className="create-form" style={{ 
            border: '1px solid #007bff', 
            padding: '20px', 
            marginBottom: '20px',
            backgroundColor: '#f8f9ff'
          }}>
            <h3>Edit Company</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Company Name
                </label>
                <input 
                  className='inputtext'  
                  value={currentName} 
                  onChange={(e) => setCurrentName(e.target.value)} 
                  placeholder='Name'
                  style={{ width: '100%' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Email
                </label>
                <input 
                  className='inputtext'  
                  value={currentEmail} 
                  onChange={(e) => setCurrentEmail(e.target.value)} 
                  placeholder='Email'
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Address
                </label>
                <input 
                  className='inputtext'  
                  value={currentAddress} 
                  onChange={(e) => setCurrentAddress(e.target.value)} 
                  placeholder='Address'
                  style={{ width: '100%' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Phone Number
                </label>
                <input 
                  className='inputtext'  
                  value={currentPhoneNumber} 
                  onChange={(e) => setCurrentPhoneNumber(e.target.value)} 
                  placeholder='Phone Number'
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className='button'  
                onClick={updateCompany}
                style={{ backgroundColor: '#007bff', color: 'white' }}
              >
                Update
              </button>
              <button 
                className='button'  
                onClick={() => setCurrentCompany(null)}
                style={{ backgroundColor: '#6c757d' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <button 
            className="button"
            onClick={sortByName}
          >
            Sort by name ({sortOrder === "asc" ? "A-Z" : "Z-A"})
          </button>
        </div>

        {/* Companies List */}
        <div className="content-wrapper content-wrapper-talent-list">
          <div className="talent-list">
            <div className="talent-item-header">
              <div style={{ flexBasis: "300px" }}>Name</div>
              <div style={{ flexBasis: "250px" }}>Email</div>
              <div style={{ flexBasis: "300px" }}>Address</div>
              <div style={{ flexBasis: "200px" }}>Phone</div>
              <div style={{ flexBasis: "100px" }}>Actions</div>
            </div>
            
            <div style={{ height: "20px" }}></div>
            
            {isLoading ? (
              <div className="loading">Loading companies...</div>
            ) : filteredCompanies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                {search ? 'No companies found matching your search.' : 'No companies created yet.'}
              </div>
            ) : (
              filteredCompanies.map((company) => (
                <div key={company.id} className="talent-item">
                  <div className="talent-item-firstline">
                    <div style={{ flexBasis: "300px", fontWeight: 'bold' }}>
                      {company.name}
                    </div>
                    <div style={{ flexBasis: "250px" }}>
                      {company.email || '-'}
                    </div>
                    <div style={{ flexBasis: "300px" }}>
                      {company.address || '-'}
                    </div>
                    <div style={{ flexBasis: "200px" }}>
                      {company.phone_number || company.phone || '-'}
                    </div>
                    <div style={{ flexBasis: "100px", display: "flex", gap: "8px" }}>
                      <button 
                        className="edit-button"
                        onClick={() => editCompany(company)}
                        title="Edit company"
                      >
                        <img src={edit} alt="Edit" className="edit-container" />
                      </button>
                      <button 
                        className="edit-button"
                        onClick={() => deleteCompany(company.id, company.name)}
                        title="Delete company"
                        style={{ backgroundColor: '#dc3545' }}
                      >
                        <img src={deleteIcon} alt="Delete" className="edit-container" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyList;
  